import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Chip, CircularProgress, Drawer, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useGetApiTokenHistoryQuery } from "../../../redux/api";

const CHIP_LIMIT = 5;
const MIN_WIDTH = 700;
const MAX_WIDTH = Math.round(window.innerWidth * 0.95);
const DEFAULT_WIDTH = 960;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatEpochMsLocal(value) {
  const ms = Number(value);
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  const hh = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
}

export default function APITokenHistoryDrawer({ open, token, onClose }) {
  const [showAllWorkflows, setShowAllWorkflows] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_WIDTH);
  const isResizing = useRef(false);
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => { setShowAllWorkflows(false); }, [token?.id]);

  // ── Resize drag handle ────────────────────────────────────────────────────

  const onMouseDown = useCallback((e) => {
    isResizing.current = true;
    e.preventDefault();

    const onMouseMove = (ev) => {
      if (!isResizing.current) return;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, ev.clientX));
      setDrawerWidth(newWidth);
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  // ── API data ───────────────────────────────────────────────────────────────

  const historyQuery = useGetApiTokenHistoryQuery(
    { tokenid: token?.id },
    { skip: !token }
  );

  const historyOk =
    historyQuery.data?.current_state === "SUCCESS" ||
    historyQuery.data?.status === "SUCCESS";

  const historyRows = useMemo(
    () => (historyOk ? (historyQuery.data?.data?.gridObject || []) : []),
    [historyOk, historyQuery.data],
  );
  const isLoading = historyQuery.isLoading || historyQuery.isFetching;

  const sortedHistoryRows = useMemo(() => {
    const rows = Array.isArray(historyRows) ? [...historyRows] : [];
    rows.sort((a, b) => {
      const av = Number(a?.date) || 0;
      const bv = Number(b?.date) || 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [historyRows, sortDir]);

  const allWorkflows = token?.assignedWorkflows || [];
  const visibleWorkflows = showAllWorkflows ? allWorkflows : allWorkflows.slice(0, CHIP_LIMIT);
  const overflow = allWorkflows.length - CHIP_LIMIT;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <Box
        sx={{
          width: drawerWidth,
          maxWidth: "95vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#e9e9e9",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Resize handle */}
        <Box
          onMouseDown={onMouseDown}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 6,
            height: "100%",
            cursor: "col-resize",
            zIndex: 10,
            bgcolor: "transparent",
            "&:hover": { bgcolor: "rgba(29,61,143,0.18)" },
            "&:active": { bgcolor: "rgba(29,61,143,0.3)" },
            transition: "background-color 0.15s",
          }}
        />

        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0f4c81",
            px: 1.25,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexShrink: 0,
            position: "relative",
            zIndex: 11,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#d2d7e0" }}>
            API Token History
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              color: "#c8d0e0",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Sub-header: token info */}
        <Box
          sx={{
            px: 2,
            py: 0.9,
            borderBottom: "1px solid #bdbdbd",
            bgcolor: "#f4f4f4",
            color: "#4b5566",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.5,
          }}
        >
          <Typography variant="body2">
            <b>Token Name:</b> {token?.name || "—"}
          </Typography>
          <Typography variant="body2">
            <b>Created By:</b> {token?.created_by || "—"}
          </Typography>
          <Typography variant="body2">
            <b>Status:</b> {token?.status || "—"}
          </Typography>
          <Typography variant="body2">
            <b>Date:</b> {token?.date || "—"}
          </Typography>

          {allWorkflows.length > 0 && (
            <Box sx={{ gridColumn: "1 / -1", mt: 0.25 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>Assigned Workflows:</b>
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                {visibleWorkflows.map((wf, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={`${wf.workflowGroup} / ${wf.workflowName}`}
                    sx={{ fontSize: "0.75rem", bgcolor: "#e8edf5", color: "#3a4a6b", border: "1px solid #c5cedf" }}
                  />
                ))}
                {!showAllWorkflows && overflow > 0 && (
                  <Tooltip
                    title={allWorkflows.slice(CHIP_LIMIT).map((wf) => `${wf.workflowGroup} / ${wf.workflowName}`).join("\n")}
                    placement="top"
                    componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
                  >
                    <Chip
                      size="small"
                      label={`+${overflow} more`}
                      onClick={() => setShowAllWorkflows(true)}
                      sx={{ fontSize: "0.75rem", bgcolor: "#dde3ee", color: "#3a4a6b", border: "1px solid #b8c3d8", cursor: "pointer", fontWeight: 700 }}
                    />
                  </Tooltip>
                )}
                {showAllWorkflows && overflow > 0 && (
                  <Chip
                    size="small"
                    label="Show less"
                    onClick={() => setShowAllWorkflows(false)}
                    sx={{ fontSize: "0.75rem", bgcolor: "#dde3ee", color: "#3a4a6b", border: "1px solid #b8c3d8", cursor: "pointer", fontWeight: 700 }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Box>

        {/* History table */}
        <Box sx={{ flex: 1, overflow: "auto", px: 1, py: 1 }}>
          {isLoading ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ minWidth: 660 }}>
              {/* Table header */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "44px 160px 150px 200px 1fr 190px",
                  bgcolor: "#1d3d8f",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  border: "1px solid #2449a8",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Box sx={{ px: 1, py: 0.75 }}>#</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Name</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Remote IP</Box>
                <Box sx={{ px: 1, py: 0.75 }}>Remote Host</Box>
                <Box sx={{ px: 1, py: 0.75 }}>API Details</Box>
                <Box
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  sx={{
                    px: 1,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  Date
                  {sortDir === "asc" ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14, opacity: 0.95 }} />
                  ) : (
                    <ArrowDownwardIcon sx={{ fontSize: 14, opacity: 0.95 }} />
                  )}
                </Box>
              </Box>

              {sortedHistoryRows.map((row, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "44px 160px 150px 200px 1fr 190px",
                    borderLeft: "1px solid #d0d0d0",
                    borderRight: "1px solid #d0d0d0",
                    borderBottom: "1px solid #d0d0d0",
                    bgcolor: idx % 2 === 0 ? "#fff" : "#f9fafc",
                    fontSize: "0.82rem",
                    color: "#4b5566",
                    alignItems: "start",
                    "&:hover": { bgcolor: "#f0f3f8" },
                  }}
                >
                  <Box sx={{ px: 1, py: 0.75 }}>{idx + 1}</Box>

                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.name}
                  </Box>

                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.ip}
                  </Box>

                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.host}
                  </Box>

                  {/* API Details — full text, wraps, monospace */}
                  <Tooltip title={row.details || ""} placement="top-start" enterDelay={600}>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.75,
                        fontFamily: "monospace",
                        fontSize: "0.78rem",
                        wordBreak: "break-all",
                        color: "#1d3d8f",
                      }}
                    >
                      {row.details}
                    </Box>
                  </Tooltip>

                  <Box sx={{ px: 1, py: 0.75, whiteSpace: "nowrap" }}>
                    {formatEpochMsLocal(row.date)}
                  </Box>
                </Box>
              ))}

              {!sortedHistoryRows.length && !isLoading && (
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #d0d0d0",
                    borderTop: 0,
                    bgcolor: "#fff",
                    color: "text.secondary",
                  }}
                >
                  No history found for this token.
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
